import is from '@sindresorhus/is';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import type { PendingCommentTarget } from './active-thread-store';
import { resolveContentRoot } from './resolve-content-root';

const collectPendingMarkIds = (pendingTarget: PendingCommentTarget | null): ReadonlySet<string> => {
    const ids = new Set<string>();
    if (!is.null(pendingTarget) && pendingTarget.target.kind === 'description-section') {
        ids.add(pendingTarget.commentId);
    }
    return ids;
};

const applyMarkStates = (
    contentEl: HTMLElement,
    activeThreadId: string | null,
    pendingMarkIds: ReadonlySet<string>,
): void => {
    const marks = contentEl.querySelectorAll<HTMLElement>('mark[data-comment-id]');
    for (const mark of marks) {
        const id = mark.getAttribute('data-comment-id');
        mark.classList.toggle('is-active', !is.null(activeThreadId) && id === activeThreadId);
        mark.classList.toggle('is-pending', !is.null(id) && pendingMarkIds.has(id));
    }
};

export const useDescriptionMarkStates = (itemId: string): void => {
    const activeThreadId = useActiveThreadStore((state) => state.activeThreadId);
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);

    const pendingMarkIds = useMemo(() => collectPendingMarkIds(pendingTarget), [pendingTarget]);

    const apply = useCallback(() => {
        const contentEl = resolveContentRoot(itemId);
        if (is.null(contentEl)) return;
        applyMarkStates(contentEl, activeThreadId, pendingMarkIds);
    }, [itemId, activeThreadId, pendingMarkIds]);

    const applyRef = useRef(apply);
    applyRef.current = apply;

    useEffect(() => {
        apply();
    }, [apply]);

    useEffect(() => {
        const contentEl = resolveContentRoot(itemId);
        if (is.null(contentEl)) return;
        const observer = new MutationObserver(() => applyRef.current());
        observer.observe(contentEl, { childList: true, characterData: true, subtree: true });
        return () => {
            observer.disconnect();
        };
    }, [itemId]);
};
